import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Settings, RefreshCw, Info, Save, X } from 'lucide-react';
import { getWhatsAppLink } from '../../utils/phoneUtils';
import { useAuth } from '../../context/AuthContext';

export interface MessageVariables {
  nombre?: string | null;
  marca?: string | null;
  puntos?: number;
  ultimavisita?: Date | null;
}

interface CustomWhatsAppMessageProps {
  countryCode: string | null;
  phoneNumber: string;
  variables: MessageVariables;
  buttonLabel?: string;
  buttonClassName?: string;
  buttonIcon?: React.ReactNode;
  defaultMessage?: string;
  storageKey?: string;
  hideEditButton?: boolean;
  isGlobalEditor?: boolean;
  onCloseEditor?: () => void;
  editorTitle?: string;
}

const CustomWhatsAppMessage: React.FC<CustomWhatsAppMessageProps> = ({
  countryCode,
  phoneNumber,
  variables,
  buttonLabel = 'Contactar por WhatsApp',
  buttonClassName = '',
  buttonIcon = <MessageSquare className="h-4 w-4 mr-1.5" />,
  defaultMessage = 'Hola @nombre, nos gustaría saber de ti.',
  storageKey = 'defaultMessageTemplate',
  hideEditButton = false,
  isGlobalEditor = false,
  onCloseEditor,
  editorTitle = 'Personalizar Mensaje'
}) => {
  const { currentUser, updateCustomMessage } = useAuth();
  const [isEditing, setIsEditing] = useState(isGlobalEditor);
  const [message, setMessage] = useState('');
  const [savedTemplate, setSavedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Variables disponibles (sintaxis @, consistente con processMessage)
  const VARIABLES: { key: string; label: string }[] = [
    { key: '@nombre', label: 'Nombre del cliente' },
    { key: '@marca', label: 'Nombre de tu negocio' },
    { key: '@puntos', label: 'Puntos acumulados' },
    { key: '@ultimavisita', label: 'Fecha de última visita' },
  ];

  // Inserta el token en la posición del cursor (o al final si no hay foco)
  const insertVariable = useCallback((token: string) => {
    const ta = textareaRef.current;
    if (!ta) { setMessage(prev => prev + token); return; }
    const start = ta.selectionStart ?? ta.value.length;
    const end = ta.selectionEnd ?? ta.value.length;
    setMessage(prev => prev.slice(0, start) + token + prev.slice(end));
    requestAnimationFrame(() => {
      ta.focus();
      const pos = start + token.length;
      ta.setSelectionRange(pos, pos);
    });
  }, []);

  const getMessageType = useCallback((key: string): string => {
    switch (key) {
      case 'birthdayMessageTemplate':
        return 'birthday';
      case 'feedbackRecoveryMessageTemplate':
        return 'feedbackRecovery';
      default:
        return key;
    }
  }, []);

  useEffect(() => {
    const messageType = getMessageType(storageKey);
    const storedMessage = currentUser?.customMessages?.[messageType];
    
    if (storedMessage) {
      setSavedTemplate(storedMessage);
    } else {
      const localStorageMessage = localStorage.getItem(storageKey);
      if (localStorageMessage) {
        setSavedTemplate(localStorageMessage);
        if (currentUser?.businessId && updateCustomMessage) {
          updateCustomMessage(messageType, localStorageMessage)
            .then(() => localStorage.removeItem(storageKey))
            .catch(err => console.error(`Failed to migrate message:`, err));
        }
      } else {
        setSavedTemplate(defaultMessage);
      }
    }
    
    if (isGlobalEditor) {
      setMessage(currentUser?.customMessages?.[messageType] || localStorage.getItem(storageKey) || defaultMessage);
    }
  }, [currentUser?.businessId, currentUser?.customMessages, storageKey, defaultMessage, isGlobalEditor, getMessageType, updateCustomMessage]);

  const processMessage = useCallback((template: string, vars: MessageVariables) => {
    let processed = template;
    const primerNombre = (vars.nombre || '').trim().split(' ')[0];
    processed = processed.replace(/@nombre/g, primerNombre || 'cliente');
    processed = processed.replace(/@marca/g, vars.marca || 'nosotros');
    processed = processed.replace(/@puntos/g, String(vars.puntos || 0));
    if (vars.ultimavisita) {
        const lastVisitStr = new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(vars.ultimavisita));
        processed = processed.replace(/@ultimavisita/g, lastVisitStr);
    }
    return processed;
  }, []);

  const handleEditClick = () => {
    setMessage(savedTemplate);
    setIsEditing(true);
  };

  const handleSaveTemplate = async () => {
    if (!message.trim()) return;
    setLoading(true);
    try {
      if (currentUser?.businessId && updateCustomMessage) {
        const messageType = getMessageType(storageKey);
        await updateCustomMessage(messageType, message);
      } else {
        localStorage.setItem(storageKey, message);
      }
      setSavedTemplate(message);
      window.dispatchEvent(new StorageEvent('storage', { key: storageKey, newValue: message, storageArea: localStorage }));
      if (isGlobalEditor && onCloseEditor) {
        onCloseEditor();
      } else {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetTemplate = () => {
    setMessage(defaultMessage);
  };
  
  const getWhatsAppMessageLink = () => {
    const finalMessage = processMessage(savedTemplate, variables);
    return getWhatsAppLink(countryCode, phoneNumber, finalMessage);
  };

  if (isEditing) {
    return (
      <div className="rounded-md border border-line p-4 bg-surface shadow-sm w-full">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-content flex items-center">
            <Settings className="h-4 w-4 mr-1.5 text-content-2" />
            {editorTitle}
          </h4>
          {isGlobalEditor && (
            <button type="button" onClick={onCloseEditor} className="text-content-3 hover:text-content-3">
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
        
        <div className="mb-3">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-line rounded-md focus:outline-none focus:ring-1 focus:ring-accent resize-none"
              rows={4}
              placeholder="Escribe tu mensaje personalizado..."
            />
        </div>

        <div className="mb-3 bg-blue-50 dark:bg-blue-500/10 rounded-md p-3 text-xs text-blue-700 dark:text-blue-300">
          <div className="flex items-start">
            <Info className="h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0 text-blue-500" />
            <div>
              <p className="font-medium mb-1.5">Toca para insertar una variable:</p>
              <div className="flex flex-wrap gap-1.5">
                {VARIABLES.map(v => (
                  <button
                    key={v.key}
                    type="button"
                    onClick={() => insertVariable(v.key)}
                    title={v.label}
                    className="font-mono bg-blue-100 dark:bg-blue-500/20 hover:bg-blue-200 dark:hover:bg-blue-500/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded border border-blue-200 dark:border-blue-500/30 transition-colors"
                  >
                    {v.key}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-3 bg-surface-2 p-3 rounded-md border border-line">
          <p className="text-xs font-medium text-content-2 mb-1">Así lo recibirá el cliente:</p>
          <p className="text-sm text-content-2 whitespace-pre-wrap">
            {processMessage(message, {
              nombre: variables.nombre || 'Juan Ejemplo',
              marca: variables.marca || currentUser?.businessName || 'Mi Negocio',
              puntos: variables.puntos ?? 100,
              ultimavisita: variables.ultimavisita || new Date(),
            })}
          </p>
        </div>
        
        <div className="flex justify-between items-center">
            <button type="button" onClick={handleResetTemplate} disabled={loading} className="inline-flex items-center px-3 py-1.5 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-surface-2 focus:outline-none disabled:opacity-50">
                <RefreshCw className="h-3.5 w-3.5 mr-1" />
                Restablecer
            </button>
            <div className="space-x-2">
                {!isGlobalEditor && ( <button type="button" onClick={() => setIsEditing(false)} disabled={loading} className="inline-flex items-center px-3 py-1.5 border border-line text-xs font-medium rounded-md shadow-sm text-content-2 bg-surface hover:bg-surface-2 focus:outline-none disabled:opacity-50"> Cancelar </button> )}
                <button type="button" onClick={handleSaveTemplate} disabled={loading} className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none disabled:opacity-50">
                    {loading ? (<> <RefreshCw className="h-3.5 w-3.5 mr-1 animate-spin" /> Guardando... </>) : (<> <Save className="h-3.5 w-3.5 mr-1" /> Guardar </>)}
                </button>
            </div>
        </div>
      </div>
    );
  }
  
  // ✅ ESTA ES LA PARTE QUE FALTABA Y CAUSABA EL ERROR
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <a 
        href={getWhatsAppMessageLink()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()} // Evita que otros eventos de click se disparen
        className={`inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none ${buttonClassName}`}
      >
        {buttonIcon}
        {buttonLabel}
      </a>
      
      {!hideEditButton && (
        <button 
          onClick={(e) => {
              e.stopPropagation(); // Evita que otros eventos de click se disparen
              handleEditClick();
          }}
          className="text-xs text-content-3 hover:text-content flex items-center"
          title="Personalizar este mensaje"
        >
          <Settings className="h-3 w-3 mr-1" />
          Personalizar
        </button>
      )}
    </div>
  );
};

export default CustomWhatsAppMessage;